import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PasswordChange() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirmPw) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      await refreshMe();
      navigate("/cases", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="password-change-page"
      className="relative flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: "#0B0B12" }}
    >
      <div className="relative z-10 w-full max-w-md">
        <div
          className="rounded-2xl border border-[color:var(--border-subtle)]/80 p-8 shadow-brand-lg"
          style={{ backgroundColor: "rgba(21, 21, 29, 0.92)" }}
        >
          <h1 className="text-xl font-semibold text-white">Update your password</h1>
          <p className="mt-2 text-sm text-[color:var(--page-fg-secondary)]">
            {user?.must_change_password
              ? "An admin issued this account a temporary password. Choose a new password to continue."
              : "Choose a new password."}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="password-change-form">
            <div className="space-y-1.5">
              <Label className="text-xs text-[color:var(--page-fg-secondary)]">Current password</Label>
              <Input
                data-testid="pwc-current"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                aria-label="Current password"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] text-white"
                style={{ backgroundColor: "#0F0F18" }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[color:var(--page-fg-secondary)]">New password</Label>
              <Input
                data-testid="pwc-new"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                aria-label="New password"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] text-white"
                style={{ backgroundColor: "#0F0F18" }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[color:var(--page-fg-secondary)]">Confirm new password</Label>
              <Input
                data-testid="pwc-confirm"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                aria-label="Confirm new password"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] text-white"
                style={{ backgroundColor: "#0F0F18" }}
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="pwc-submit"
              className="h-11 w-full rounded-xl font-semibold"
              style={{ backgroundColor: "#14B8B8", color: "#0B0B12" }}
            >
              {busy ? "Saving…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
